import { Inbox, Search, SlidersHorizontal, X } from "lucide-react";
import React from "react";

export type TabItem = {
  id: string;
  label: string;
  count?: number;
};

export function AdminPageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function AdminTabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xs text-xs font-semibold transition border ${
              isActive
                ? "bg-primary text-on-accent border-primary shadow-xs"
                : "bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`min-w-[1.125rem] h-4 px-1 rounded-xs text-xs font-bold flex items-center justify-center ${
                  isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export type FilterSelect = {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
};

export function AdminFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  searchValue,
  selects = [],
  filters,
  onSelectChange,
  onClear,
  showClear,
}: {
  search?: string;
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  selects?: FilterSelect[];
  filters?: FilterSelect[];
  onSelectChange?: (id: string, value: string) => void;
  onClear?: () => void;
  showClear?: boolean;
}) {
  return (
    <div className="surface-sm rounded-sm p-3.5 space-y-2.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <SlidersHorizontal size={13} />
        Filters
      </div>
      <div className="flex flex-wrap gap-2.5 items-end">
        {onSearchChange !== undefined && (
          <div className="flex-1 min-w-[180px] relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              value={searchValue ?? search ?? ""}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="field pl-8 pr-7 py-1.5 rounded-xs text-xs"
            />
            {Boolean(search) && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-xs hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Clear search"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}
        {(selects.length ? selects : filters || []).map((sel) => (
          <div key={sel.id} className="min-w-[120px] flex-1 sm:flex-initial">
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-0.5">
              {sel.label}
            </label>
            <select
              value={sel.value}
              onChange={(e) => onSelectChange?.(sel.id, e.target.value)}
              className="field py-1.5 px-2.5 rounded-xs text-xs bg-card"
            >
              {sel.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        ))}
        {showClear && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="btn bg-secondary text-muted-foreground hover:text-foreground text-xs px-2.5 py-1.5 rounded-xs"
          >
            <X size={13} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export function CategoryBadge({ category }: { category: string }) {
  const norm = category.toLowerCase();
  let style = "bg-secondary/80 text-foreground border-border";
  if (norm === "error") {
    style = "bg-destructive/10 text-destructive border-destructive/20";
  } else if (norm === "access") {
    style = "bg-info/10 text-info border-info/30";
  } else if (norm === "crucial") {
    style = "bg-primary/10 text-primary border-primary/30";
  }
  const label = category.charAt(0).toUpperCase() + category.slice(1);
  return (
    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-xs border ${style}`}>
      {label}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: string }) {
  const normalized = severity.toLowerCase();
  let style = "bg-secondary text-muted-foreground border-border";

  if (normalized === "success") {
    style = "bg-success/10 text-success border-success/30";
  } else if (normalized === "error") {
    style = "bg-destructive/10 text-destructive border-destructive/20";
  } else if (normalized === "warning") {
    style = "bg-warning/10 text-warning border-warning/30";
  } else if (normalized === "crucial") {
    style = "bg-primary/10 text-primary border-primary/30";
  }

  return (
    <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-xs border ${style}`}>
      {severity}
    </span>
  );
}


export function AdminEmptyState({
  message,
  description,
  title = "No Results Found",
  icon: Icon,
  action,
}: {
  message?: string;
  description?: string;
  title?: string;
  icon?: React.ElementType | React.ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  const resolvedMessage = message ?? description ?? "";
  const renderIcon = () => {
    if (React.isValidElement(Icon)) return Icon;
    if (typeof Icon === "function" || (typeof Icon === "object" && Icon !== null && !("$$typeof" in Icon))) {
      const DisplayIcon = Icon as React.ElementType;
      return <DisplayIcon size={38} className="mx-auto text-muted-foreground/50 mb-2.5" />;
    }
    return <Inbox size={38} className="mx-auto text-muted-foreground/50 mb-2.5" />;
  };

  return (
    <div className="py-12 px-6 text-center bg-card rounded-xl border border-dashed border-border m-3">
      {renderIcon()}
      {title && <h3 className="font-bold text-base text-foreground mb-1">{title}</h3>}
      {resolvedMessage && <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">{resolvedMessage}</p>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-3.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-on-accent text-xs font-semibold hover:opacity-90 transition shadow-xs"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
